import { useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import type { Station } from '@hcc/types';
import { ASSET_IDENTIFIER_FIELDS, identifierFieldsFor } from '../../lib/assetCategories';
import { AdditionalInfoEditor } from '../../components/kit/AdditionalInfoEditor';
import { Field } from './assetFormField';

const ID_KEYS = new Set<string>(ASSET_IDENTIFIER_FIELDS.map((f) => f.key));

/**
 * All of an asset's metadata inputs: the Syscode (the asset's unique CAFM
 * identifier, shown for every category), the category-appropriate
 * identifier fields (serial / IMEI / MAC / registration) plus free-form
 * additional info (Make, Model, ...) that enriches the record for the
 * CAFM export. Split out of AssetForm to keep that file under the size cap.
 *
 * The parent owns the flat `meta` map; identifiers and extras are two views
 * of it, split on the known identifier keys. Syscode is NOT part of that
 * map: it is a real column with a unique constraint, so it travels as its
 * own prop pair.
 */
export function AssetMetadataFields({
  station,
  category,
  syscode,
  onSyscodeChange,
  meta,
  setMeta,
  hydrated,
}: {
  station: Station;
  category: string;
  syscode: string;
  onSyscodeChange: (value: string) => void;
  meta: Record<string, string>;
  setMeta: Dispatch<SetStateAction<Record<string, string>>>;
  /** True once the parent has loaded an existing asset's metadata (or immediately on create). Re-seeds the additional-info editor. */
  hydrated: boolean;
}) {
  const [showAllIds, setShowAllIds] = useState(false);

  // Show only the identifier fields that make sense for this kind of kit,
  // plus any that already hold a value (so editing never hides data) and
  // everything when the operator opts into "show all" for a one-off.
  const visibleIdFields = useMemo(() => {
    if (showAllIds) return ASSET_IDENTIFIER_FIELDS;
    const relevant = new Set<string>(identifierFieldsFor(station, category));
    for (const [k, v] of Object.entries(meta)) {
      if (typeof v === 'string' && v.trim().length > 0) relevant.add(k);
    }
    return ASSET_IDENTIFIER_FIELDS.filter((f) => relevant.has(f.key));
  }, [showAllIds, station, category, meta]);

  // The additional-info editor manages every non-identifier key.
  const extraAttrs = useMemo(
    () => Object.fromEntries(Object.entries(meta).filter(([k]) => !ID_KEYS.has(k))),
    [meta],
  );
  function onExtraChange(attrs: Record<string, string>) {
    setMeta((m) => {
      const ids = Object.fromEntries(Object.entries(m).filter(([k]) => ID_KEYS.has(k)));
      return { ...ids, ...attrs };
    });
  }

  return (
    <>
      <div>
        <div className="mb-1 flex items-center justify-between gap-2 text-xs font-medium uppercase tracking-wider text-fg-muted">
          <span>Identifiers</span>
          {!showAllIds && visibleIdFields.length < ASSET_IDENTIFIER_FIELDS.length ? (
            <button
              type="button"
              onClick={() => setShowAllIds(true)}
              className="text-[10px] font-medium normal-case tracking-normal text-accent hover:underline"
            >
              Show all
            </button>
          ) : null}
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {/* Every asset gets a Syscode slot regardless of category: it is
              the unique key the CAFM (Planon) integration matches on. */}
          <Field label="Syscode" htmlFor="meta-syscode" size="sm">
            <input
              id="meta-syscode"
              value={syscode}
              onChange={(e) => onSyscodeChange(e.target.value)}
              placeholder="CAFM asset code (unique)"
              className="font-mono"
            />
          </Field>
          {visibleIdFields.map((f) => (
            <Field key={f.key} label={f.label} htmlFor={`meta-${f.key}`} size="sm">
              <input
                id={`meta-${f.key}`}
                value={meta[f.key] ?? ''}
                onChange={(e) => setMeta((m) => ({ ...m, [f.key]: e.target.value }))}
                placeholder={f.placeholder}
                className="font-mono"
              />
            </Field>
          ))}
        </div>
      </div>

      {/* Free-form extras beyond the fixed identifiers, e.g. Make / Model, so
          the CAFM export has them. Optional; tucked behind a button. Keyed on
          `hydrated` so an edited asset's saved extras seed in once loaded. */}
      <AdditionalInfoEditor
        key={hydrated ? 'loaded' : 'empty'}
        value={extraAttrs}
        onChange={onExtraChange}
      />
    </>
  );
}
